// src/components/GroupChatWindow.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ChatRoom } from './ChatRoomsPanel';
import { X, Send, Minimize2, GripVertical, MessageSquare, Paperclip, File, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { DocumentViewer } from './DocumentViewer';
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications';
import { useEntity } from '@/hooks/useEntity';
import { sharedFileEntityConfig, type SharedFile } from '@/entities/SharedFile';
import { supabase } from '@/lib/supabase';

interface GroupMessage {
  id: string;
  room_id: string;
  from_user_id: string;
  from_user_name: string;
  message: string;
  timestamp: string;
  file?: {
    name: string;
    url: string;
    type: string;
  };
}

interface GroupChatWindowProps {
  room: ChatRoom;
  onClose: () => void;
  isMinimized: boolean;
  onMinimize: () => void;
}

export function GroupChatWindow({ room, onClose, isMinimized, onMinimize }: GroupChatWindowProps) {
  const { user: currentUser } = useAuth();
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string; type: string } | null>(null);
  const [position, setPosition] = useState({ 
    x: window.innerWidth - 400, 
    y: window.innerHeight - 520 
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previousMessageCountRef = useRef(0);
  const { showNotification } = useBrowserNotifications();
  const { create: createSharedFile } = useEntity<SharedFile>(sharedFileEntityConfig);

  const uploadFile = async (file: File, key: string) => {
    const bucket = 'shared-files';
    const path = `${currentUser?.id || 'anon'}/${key}-${file.name}`;
    try {
      const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: false });
      if (error) throw error;
      const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);
      if (publicData?.publicUrl) return publicData.publicUrl;
    } catch (err) {
      console.error('Supabase upload failed, falling back to data URL', err);
    }
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  };

  useEffect(() => {
    const savedPosition = localStorage.getItem(`whipt_group_chat_position_${room.id}`);
    if (savedPosition) {
      setPosition(JSON.parse(savedPosition));
    }
  }, [room.id]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (chatRef.current && !isMinimized) {
      const rect = chatRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        const maxX = window.innerWidth - (chatRef.current?.offsetWidth || 384);
        const maxY = window.innerHeight - (chatRef.current?.offsetHeight || 500);
        
        const boundedX = Math.max(0, Math.min(newX, maxX));
        const boundedY = Math.max(0, Math.min(newY, maxY));
        
        setPosition({ x: boundedX, y: boundedY });
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        localStorage.setItem(`whipt_group_chat_position_${room.id}`, JSON.stringify(position));
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, position, room.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  // Load and poll for messages
  useEffect(() => {
    const loadMessages = () => {
      const messagesKey = `whipt_room_messages_${room.id}`;
      const stored = localStorage.getItem(messagesKey);
      
      if (stored) {
        try {
          const parsedMessages = JSON.parse(stored);
          
          // Check for new messages
          if (isMinimized && parsedMessages.length > previousMessageCountRef.current) {
            const newMessagesFromOthers = parsedMessages.slice(previousMessageCountRef.current)
              .filter((msg: GroupMessage) => msg.from_user_id !== currentUser?.id);
            
            if (newMessagesFromOthers.length > 0) {
              setHasNewMessages(true);
              // Show browser notification
              const latestMessage = newMessagesFromOthers[newMessagesFromOthers.length - 1];
              showNotification(
                `${latestMessage.from_user_name} in ${room.name}`,
                {
                  body: latestMessage.file ? '📎 Shared a file' : latestMessage.message,
                  tag: room.id
                }
              );
            }
          }
          
          previousMessageCountRef.current = parsedMessages.length;
          setMessages(parsedMessages);
        } catch (error) {
          console.error('Failed to parse messages:', error);
        }
      }
      
      // Mark as read if not minimized
      if (!isMinimized && currentUser) {
        const lastReadKey = `whipt_room_last_read_${room.id}_${currentUser.id}`;
        localStorage.setItem(lastReadKey, new Date().toISOString());
      }
    };

    loadMessages();
    const interval = setInterval(loadMessages, 3000);

    return () => clearInterval(interval);
  }, [room.id, currentUser, isMinimized]);

  // Clear flash when window is opened
  useEffect(() => {
    if (!isMinimized) {
      setHasNewMessages(false);
    }
  }, [isMinimized]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentUser || isSending) return;

    setIsSending(true);

    try {
      const message: GroupMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        room_id: room.id,
        from_user_id: currentUser.id,
        from_user_name: currentUser.name,
        message: newMessage.trim(),
        timestamp: new Date().toISOString()
      };

      const messagesKey = `whipt_room_messages_${room.id}`;
      const stored = localStorage.getItem(messagesKey);
      const existingMessages = stored ? JSON.parse(stored) : [];
      const updatedMessages = [...existingMessages, message];
      localStorage.setItem(messagesKey, JSON.stringify(updatedMessages));

      setMessages(updatedMessages);
      setNewMessage('');
      
      // Mark as read
      const lastReadKey = `whipt_room_last_read_${room.id}_${currentUser.id}`;
      localStorage.setItem(lastReadKey, new Date().toISOString());
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    const key = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileUrl = await uploadFile(file, key);

    const fileMessage: GroupMessage = {
      id: key,
      room_id: room.id,
      from_user_id: currentUser.id,
      from_user_name: currentUser.name,
      message: `📎 Shared a file: ${file.name}`,
      timestamp: new Date().toISOString(),
      file: {
        name: file.name,
        url: fileUrl,
        type: file.type
      }
    };

    const messagesKey = `whipt_room_messages_${room.id}`;
    const stored = localStorage.getItem(messagesKey);
    const existingMessages = stored ? JSON.parse(stored) : [];
    const updatedMessages = [...existingMessages, fileMessage];
    localStorage.setItem(messagesKey, JSON.stringify(updatedMessages));

    setMessages(updatedMessages);
    // Persist shared file metadata for cross-session access
    createSharedFile({
      userId: currentUser.id,
      roomId: room.id,
      roomName: room.name,
      name: file.name,
      url: fileUrl,
      type: file.type,
      sharedBy: currentUser.id,
      sharedByName: currentUser.name,
      timestamp: fileMessage.timestamp,
    } as any);
    
    // Mark as read
    const lastReadKey = `whipt_room_last_read_${room.id}_${currentUser.id}`;
    localStorage.setItem(lastReadKey, new Date().toISOString());
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      amber: 'from-amber-500 to-orange-500',
      blue: 'from-blue-500 to-cyan-500',
      purple: 'from-purple-500 to-pink-500',
      green: 'from-green-500 to-emerald-500',
      red: 'from-red-500 to-rose-500',
    };
    return colors[color] || colors.amber;
  };

  if (!currentUser) return null;

  // Minimized state
  if (isMinimized) {
    return (
      <button
        onClick={onMinimize}
        style={{ left: position.x, top: position.y }}
        className={`fixed z-50 px-4 py-3 rounded-t-2xl bg-gradient-to-r ${getColorClasses(room.color)} text-white font-semibold shadow-xl border-2 border-white/20 hover:shadow-2xl transition-all ${
          hasNewMessages ? 'animate-pulse' : ''
        }`}
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          <span className="text-sm">{room.name}</span>
          {hasNewMessages && (
            <span className="ml-2 w-2 h-2 bg-red-500 rounded-full animate-ping" />
          )}
        </div>
      </button>
    );
  }

  return (
    <div 
      ref={chatRef}
      style={{ left: position.x, top: position.y }}
      className={`fixed z-50 w-96 max-w-[calc(100vw-2rem)] ${isDragging ? 'cursor-grabbing' : ''}`}
    >
      <div className={`backdrop-blur-xl bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-2xl border-2 border-amber-400/30 shadow-[0_20px_70px_rgba(251,191,36,0.3)] overflow-hidden flex flex-col ${isCollapsed ? 'h-auto' : 'h-[500px]'}`}>
        {/* Header - Draggable */}
        <div 
          onMouseDown={handleMouseDown}
          className={`bg-gradient-to-r ${getColorClasses(room.color)} px-4 py-3 flex items-center justify-between cursor-grab active:cursor-grabbing`}
        >
          <div className="flex items-center gap-3">
            <GripVertical className="w-4 h-4 text-white/70" />
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${getColorClasses(room.color)} flex items-center justify-center ring-2 ring-white/30`}>
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">{room.name}</h3>
              <p className="text-xs text-white/80">{room.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title={isCollapsed ? "Expand chat" : "Collapse chat"}
            >
              <ChevronDown className={`w-4 h-4 text-white transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={onMinimize}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Minimize"
            >
              <Minimize2 className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Messages */}
        {!isCollapsed && (
        <>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No messages yet</p>
              <p className="text-xs mt-1">Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isFromMe = msg.from_user_id === currentUser.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[75%]`}>
                    {!isFromMe && (
                      <p className="text-xs text-slate-400 mb-1 px-2">{msg.from_user_name}</p>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2 ${
                        isFromMe
                          ? `bg-gradient-to-r ${getColorClasses(room.color)} text-white`
                          : 'bg-slate-700/70 text-slate-100'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                      {msg.file && (
                        <button
                          onClick={() => setViewingFile(msg.file!)}
                          className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg ${
                            isFromMe ? 'bg-white/20 hover:bg-white/30' : 'bg-slate-600/50 hover:bg-slate-600/70'
                          } transition-colors`}
                        >
                          {msg.file.type.startsWith('image/') ? (
                            <ImageIcon className="w-4 h-4" />
                          ) : (
                            <File className="w-4 h-4" />
                          )}
                          <span className="text-xs truncate max-w-[200px]">{msg.file.name}</span>
                        </button>
                      )}
                      <p className={`text-xs mt-1 ${isFromMe ? 'text-white/70' : 'text-slate-400'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 bg-slate-900/50 border-t border-slate-700/50">
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-amber-400"
              title="Attach file"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              disabled={isSending}
              className="flex-1 px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm disabled:opacity-50"
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || isSending}
              className={`px-4 py-2 rounded-xl bg-gradient-to-r ${getColorClasses(room.color)} text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
        </>
        )}
      </div>

      {/* Document Viewer */}
      {viewingFile && (
        <DocumentViewer
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  );
}
